import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Pusher from 'pusher-js';
import {
  ChefHat,
  CheckCircle2,
  Boxes,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  Trash2,
  LayoutGrid,
  LogOut,
  Loader2,
  Users,
  ShoppingBag,
  Clock3,
  UserRound,
  StickyNote,
  Check,
  Undo2,
  Store,
  X,
  ChevronDown,
  ChevronUp,
  Moon,
  Sun,
  Minus,
  Plus,
  Type,
} from 'lucide-react';
import { LRUCache } from 'lru-cache';
import { API_BASE } from '../apiBase';
import { clearSession } from '../auth';
import { applyTheme, getStoredTheme } from '../theme';
import { getBusinessDay } from '../businessDay';
import { getCanonicalOrderDate, getOrderAgeMinutes } from '../utcTime';
import {
  applyCardTextPx,
  CARD_TEXT_PX_MAX,
  CARD_TEXT_PX_MIN,
  getStoredCardTextPx,
  stepCardTextPx,
} from '../textScale';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const isCanceledProduct = (product) =>
  String(product?.statut_ticket || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .includes('annul');

const CANCELED_STRIKE_STYLE = {
  color: '#dc2626',
  textDecorationLine: 'line-through',
  textDecorationColor: '#dc2626',
  textDecorationThickness: '2.5px',
  textDecorationSkipInk: 'none',
};

const PREPARED_STRIKE_STYLE = {
  color: '#94a3b8',
  textDecorationLine: 'line-through',
  textDecorationColor: '#4ade80',
  textDecorationThickness: '1px',
  textDecorationSkipInk: 'none',
};

/** Fixed ticket card height — product list area fits ~5 lines; scroll when there are more. */
const ORDER_CARD_HEIGHT_CLASS = 'kd-order-card';
/** ~5 product rows visible; remainder scrolls inside the card. */
const ORDER_CARD_VISIBLE_PRODUCTS = 5;
const ORDER_CARD_PRODUCTS_SCROLL_CLASS = 'kd-card-products kd-card-scroll pr-0.5';
const ORDER_CARD_GRID_CLASS =
  'kd-order-grid grid w-full grid-cols-1 items-stretch px-[5px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5';

const OrderCardHeader = ({ order, theme, ticketTime, timerMinutes, className }) => {
  const dineIn = order.surplace === 'Sur place';
  const covers = order.nb_covered || 'N/A';
  const compteur = Number(order.compteur);
  const displayedTicketNumber = Number.isFinite(compteur) && compteur > 0 ? compteur : order.id_ticket;

  return (
    <div className={cx('kd-card-header border-b', theme.header, className)}>
      <div className="kd-card-header-top">
        <div className="kd-card-header-ticket min-w-0">
          <p className="kd-card-ticket-label text-slate-400 dark:text-neutral-300">Ticket</p>
          <div className="kd-card-ticket-row">
            <span className={cx('kd-card-ticket-num', theme.ticket)}>#{displayedTicketNumber}</span>
            {ticketTime ? (
              <span className="kd-card-header-clock kd-card-chip-sm inline-flex items-center gap-1 font-bold tabular-nums text-slate-600 dark:text-neutral-100">
                <Clock3 className="kd-card-icon-sm" strokeWidth={2.5} aria-hidden="true" />
                {ticketTime}
              </span>
            ) : null}
          </div>
        </div>

        <div className="kd-card-header-side" aria-label="Boutique et timer">
          <span
            className={cx(
              'kd-card-chip-xs kd-card-shop-chip inline-flex items-center gap-1 truncate rounded-lg px-[0.5em] py-[0.25em] uppercase tracking-wide',
              theme.chip
            )}
            title={order.nom_boutique || 'N/A'}
          >
            <Store className="kd-card-icon-sm shrink-0" strokeWidth={2.25} aria-hidden="true" />
            <span className="truncate">{order.nom_boutique || 'N/A'}</span>
          </span>
          <div
            className={cx('kd-card-timer inline-flex items-center gap-[0.35em] rounded-xl', theme.timer)}
            title="Temps d'attente"
          >
            <Clock3 className="kd-card-icon-md shrink-0" strokeWidth={2.5} aria-hidden="true" />
            <span className="kd-card-timer-value">
              {Number.isFinite(timerMinutes) ? `${timerMinutes} min` : '-- min'}
            </span>
          </div>
        </div>
      </div>

      <div className="kd-card-header-meta">
        <span
          className={cx(
            'kd-card-chip-xs kd-card-type-chip inline-flex items-center gap-1 rounded-lg px-[0.45em] py-[0.2em]',
            theme.chip
          )}
        >
          {dineIn ? (
            <Users className="kd-card-icon-sm" strokeWidth={2.25} aria-hidden="true" />
          ) : (
            <ShoppingBag className="kd-card-icon-sm" strokeWidth={2.25} aria-hidden="true" />
          )}
          {order.surplace}
        </span>
        <span className="kd-card-chip-sm inline-flex items-center gap-1 rounded-lg bg-white/60 px-[0.45em] py-[0.2em] font-semibold text-slate-600 ring-1 ring-black/[0.05] dark:bg-neutral-900/60 dark:text-neutral-100 dark:ring-white/10">
          <Users className="kd-card-icon-sm" strokeWidth={2.25} aria-hidden="true" />
          {covers} couv.
        </span>
      </div>
    </div>
  );
};

// Order boards share one segmented control. Produits is a different kind of board and
// sits as its own control next to them, so cooks never confuse "ticket mode" with
// "aggregate products".
const ORDER_VIEWS = [
  { id: 'in-progress', label: 'En cours', status: 'Commandes en cours', Icon: ChefHat },
  { id: 'ready', label: 'Prêtes', status: 'Commandes prêtes', Icon: CheckCircle2 },
];

const VIEW_STATUS = {
  'in-progress': 'Commandes en cours',
  ready: 'Commandes prêtes',
  products: 'Produits à préparer',
};

const VIEW_DOT = {
  'in-progress': 'bg-kd-ok',
  ready: 'bg-kd-brand',
  products: 'bg-kd-warn',
};

const canalLogos = {
  GLOVO: '/glovo.png',
  LALIVRAISON: 'https://lalivraison.ma/faviconLalivraison.png',
  Yassir: 'https://yassir.com/images/common/favicon-yassir-forward.png',
  kaalix: 'https://kaalix.com/favicon.ico',
  Livry: 'https://livry.ma/favicon.ico',
  BORNE: 'https://bornety.ma/wp-content/uploads/2023/09/icon-bornety.png',
};

const defaultLogo = '/siteQ.png';

const cardActionBtnClass = (busy, tone) =>
  cx(
    'kd-card-action-btn relative inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl px-3 text-white shadow-md transition hover:brightness-105 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60',
    tone === 'ready' && 'bg-gradient-to-r from-emerald-500 to-green-600',
    tone === 'back' && 'bg-gradient-to-r from-sky-500 to-blue-600',
    busy && 'text-transparent'
  );

const OrderCardProductsScroll = ({ scrollNeeded, layoutKey, className, children }) => {
  const scrollRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const updateScrollHint = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      setShowScrollHint(false);
      return;
    }
    const canScroll = el.scrollHeight - el.clientHeight > 4;
    if (!scrollNeeded || !canScroll) {
      setShowScrollHint(false);
      return;
    }
    setShowScrollHint(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  }, [scrollNeeded]);

  useEffect(() => {
    updateScrollHint();
    const el = scrollRef.current;
    if (!el) return undefined;

    const timer = window.setTimeout(updateScrollHint, 0);

    el.addEventListener('scroll', updateScrollHint, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollHint);
    resizeObserver.observe(el);
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild);
    }

    return () => {
      window.clearTimeout(timer);
      el.removeEventListener('scroll', updateScrollHint);
      resizeObserver.disconnect();
    };
  }, [updateScrollHint, children, layoutKey]);

  return (
    <div className="kd-card-products-scroll-wrap">
      <div
        ref={scrollRef}
        className={className}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        {children}
      </div>
      {showScrollHint ? (
        <div className="kd-card-products-scroll-hint" aria-hidden="true">
          <div className="flex items-center gap-0.5 rounded-full bg-white/90 px-2.5 py-1 shadow-sm ring-1 ring-slate-200/90 dark:bg-neutral-900 dark:ring-neutral-600/80">
            <ChevronDown
              className="h-4 w-4 animate-kd-scroll-hint text-slate-600 dark:text-neutral-100"
              strokeWidth={2.75}
              aria-hidden="true"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const OrderCardFooter = ({ order, theme, busy, onReady, onBack, className }) => (
  <div className={cx('kd-card-pad-footer flex items-center gap-[0.5em] border-t', theme.footer, className)}>
    <span className="kd-card-footer-text inline-flex min-w-0 flex-1 items-center gap-1 text-slate-600 dark:text-neutral-100">
      <UserRound className="kd-card-icon-sm shrink-0" strokeWidth={2.25} aria-hidden="true" />
      <span className="truncate">{order.nom_serveur || 'N/A'}</span>
    </span>
    {order.canal ? (
      <img
        src={canalLogos[order.canal] || defaultLogo}
        alt={`${order.canal || 'Unknown'} logo`}
        className="kd-card-canal-logo rounded-md bg-white object-contain p-0.5 shadow-sm ring-1 ring-black/5"
      />
    ) : null}
    {!order.isReady ? (
      <button type="button" className={cardActionBtnClass(busy, 'ready')} onClick={onReady} disabled={busy}>
        {busy ? (
          <Loader2 className="absolute h-5 w-5 animate-kd-spin text-white" aria-hidden="true" />
        ) : (
          <>
            <Check className="kd-card-icon-md shrink-0" strokeWidth={2.5} aria-hidden="true" />
            Prêt
          </>
        )}
      </button>
    ) : (
      <button type="button" className={cardActionBtnClass(busy, 'back')} onClick={onBack} disabled={busy}>
        {busy ? (
          <Loader2 className="absolute h-5 w-5 animate-kd-spin text-white" aria-hidden="true" />
        ) : (
          <>
            <Undo2 className="kd-card-icon-md shrink-0" strokeWidth={2.5} aria-hidden="true" />
            Retour
          </>
        )}
      </button>
    )}
  </div>
);

const getCacheKey = (menuName, endpoint, params) => {
  const paramString = JSON.stringify({
    menuName,
    id_caisse: params.id_caisse,
    id_account_caisse: params.id_account_caisse,
    current_day: params.current_day,
  });
  return `${endpoint}:${paramString}`;
};

// Initialize LRU Cache
const cache = new LRUCache({
  max: 100,
  ttl: 5 * 60 * 1000,
  updateAgeOnGet: false,
});

const ORDERS_ENDPOINT = `${API_BASE}/orders-by-menu`;
const READY_ORDERS_ENDPOINT = `${API_BASE}/ready-orders-by-menu`;
const PRODUCTS_ENDPOINT = `${API_BASE}/products-to-prepare`;

const PUSHER_KEY = 'de6c3146173255173c44';
const PUSHER_CLUSTER = 'eu';
// Both names are handled: the backend emits `notify-order-update`, and `order-updated` was
// previously bound to an empty function so anything sent under that name was silently discarded.
const ORDER_EVENTS = ['notify-order-update', 'order-updated'];

// Pusher never replays events that were missed while the socket was down, so the board is
// also polled. Slow cadence while the realtime link is healthy (pure safety net), fast
// cadence while it is not (the only thing keeping the screen current).
const POLL_INTERVAL_HEALTHY_MS = 30000;
const POLL_INTERVAL_DEGRADED_MS = 5000;
// Every display in a restaurant starts up and reconnects at roughly the same moment, so a
// fixed interval makes them all query on the same tick forever. A little spread avoids that.
const POLL_JITTER_MS = 2000;
// When a fetch fails, back off instead of waiting for the next scheduled poll.
const RETRY_BASE_DELAY_MS = 1000;
const RETRY_MAX_DELAY_MS = 10000;
// A poll right after an event-driven refresh would only repeat the same heavy query.
const MIN_REFRESH_SPACING_MS = 3000;
// Guards against two stacked events playing the notification sound twice.
const MIN_SOUND_SPACING_MS = 1500;

// A healthy screen resyncs at least every POLL_INTERVAL_HEALTHY_MS, so anything older than this
// means pushes and polling are both failing and what is on screen can no longer be trusted.
const FRESHNESS_WARNING_MS = 60000;
const FRESHNESS_CRITICAL_MS = 120000;

// AudioService (unchanged)
class AudioService {
  constructor() {
    this.audioElement = new Audio('/notification.mp3');
    this.audioElement.preload = 'auto';
    this.audioElement.loop = false;
    this.audioContext = null;
    this.soundBuffer = null;
    this.isUnlocked = false;
    this.userInteracted = false;
    this.pendingPlay = false;

    this.bindInteractionEvents();
  }

  bindInteractionEvents() {
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach((event) => {
      document.addEventListener(event, this.handleInteraction.bind(this), { once: false });
    });
  }

  handleInteraction() {
    this.userInteracted = true;
    this.unlock();
    if (this.pendingPlay) {
      this.playWithRetry();
      this.pendingPlay = false;
    }
  }

  async unlock() {
    if (this.isUnlocked) return true;

    try {
      this.audioElement.muted = true;
      await this.audioElement.play();
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      this.audioElement.muted = false;
      this.isUnlocked = true;
      return true;
    } catch (err) {
      return await this.initWebAudio();
    }
  }

  async initWebAudio() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      const response = await fetch('/notification.mp3');
      const arrayBuffer = await response.arrayBuffer();
      this.soundBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.isUnlocked = true;
      return true;
    } catch (err) {
      console.error('Web Audio API initialization failed:', err);
      return false;
    }
  }

  playWebAudio() {
    if (!this.soundBuffer || !this.audioContext) return false;
    try {
      const source = this.audioContext.createBufferSource();
      source.buffer = this.soundBuffer;
      source.connect(this.audioContext.destination);
      source.start(0);
      return true;
    } catch (err) {
      console.error('Web Audio playback failed:', err);
      return false;
    }
  }

  async play() {
    if (!this.userInteracted) {
      this.pendingPlay = true;
      return false;
    }

    if (!this.isUnlocked) {
      await this.unlock();
      if (!this.isUnlocked) {
        return false;
      }
    }

    try {
      this.audioElement.currentTime = 0;
      await this.audioElement.play();
      return true;
    } catch (err) {
      if (this.audioContext && this.soundBuffer) {
        return this.playWebAudio();
      }
      return false;
    }
  }

  async playWithRetry(maxRetries = 3, delayMs = 500) {
    let attempts = 0;
    while (attempts < maxRetries) {
      if (await this.play()) {
        return true;
      }
      attempts++;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    console.error('Failed to play audio after maximum retries');
    return false;
  }
}

const audioService = new AudioService();

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const clearCache = (endpoint) => {
  const keys = Array.from(cache.keys()).filter((key) => key.startsWith(endpoint));
  keys.forEach((key) => cache.delete(key));
};

const clearOrderCaches = () => {
  clearCache(ORDERS_ENDPOINT);
  clearCache(READY_ORDERS_ENDPOINT);
  clearCache(PRODUCTS_ENDPOINT);
};

const formatAge = (ms) => {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h`;
};

// A stale board is indistinguishable from a current one, which is how a failure reaches the
// customer before it reaches the staff. This makes the board's age visible at a glance.
// It owns its own once-per-second clock so the ticking never re-renders the order cards.
const FreshnessIndicator = ({ lastUpdatedAt }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!lastUpdatedAt) {
    return (
      <div className="rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-kd-danger animate-kd-pulse-dot dark:bg-red-950/50 dark:text-red-400">
        En attente de données...
      </div>
    );
  }

  const age = Math.max(0, now - lastUpdatedAt);
  const level =
    age >= FRESHNESS_CRITICAL_MS ? 'critical' : age >= FRESHNESS_WARNING_MS ? 'warning' : 'fresh';

  const tone =
    level === 'fresh'
      ? 'bg-emerald-50 text-kd-ok dark:bg-emerald-950/40 dark:text-emerald-400'
      : level === 'warning'
        ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
        : 'bg-red-50 text-kd-danger animate-kd-pulse-dot dark:bg-red-950/40 dark:text-red-400';

  return (
    <div
      className={cx('rounded-full px-3 py-1 text-xs font-semibold', tone)}
      title="Temps écoulé depuis la dernière synchronisation réussie avec le serveur"
    >
      {level === 'fresh'
        ? `Synchronisé il y a ${formatAge(age)}`
        : `Non synchronisé depuis ${formatAge(age)}`}
    </div>
  );
};

const KitchenDisplay = () => {
  const { menuName } = useParams();
  const navigate = useNavigate();
  const [inProgressOrders, setInProgressOrders] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingInProgress, setLoadingInProgress] = useState(true);
  const [loadingReady, setLoadingReady] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [isInitialOrToggleFetch, setIsInitialOrToggleFetch] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderTimer, setSelectedOrderTimer] = useState(0);
  const [showReadyOrders, setShowReadyOrders] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [buttonLoading, setButtonLoading] = useState({});
  const [isSwitching, setIsSwitching] = useState(false);
  const [pendingView, setPendingView] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  // Persisted so a kitchen that muted the alert stays muted across the reloads that
  // happen on deploys, crashes and shift changes.
  const [isSoundEnabled, setIsSoundEnabled] = useState(
    () => localStorage.getItem('kds_sound_enabled') !== 'false'
  );
  const [showToast, setShowToast] = useState(false);
  const [showAudioPrompt, setShowAudioPrompt] = useState(true);
  const [pendingFetches, setPendingFetches] = useState(0);
  const [isMarkingAllReady, setIsMarkingAllReady] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [colorTheme, setColorTheme] = useState(() => getStoredTheme());
  const [cardTextPx, setCardTextPx] = useState(() => getStoredCardTextPx());
  // Collapsed "Voir préparés" sections on En cours cards that reappeared with new lines.
  const [expandedHistoryIds, setExpandedHistoryIds] = useState({});

  // The realtime subscription reads these refs instead of taking the values as effect
  // dependencies. Previously the Pusher client was destroyed and rebuilt whenever the operator
  // toggled the sound or the products view, and any order announced during that gap was lost for
  // good because Pusher does not replay missed events.
  const isSoundEnabledRef = useRef(isSoundEnabled);
  const showProductsRef = useRef(showProducts);
  const refreshAllRef = useRef(() => Promise.resolve());
  const refreshInFlightRef = useRef(false);
  const refreshQueuedRef = useRef(false);
  const lastRefreshAtRef = useRef(0);
  const lastSoundAtRef = useRef(0);
  const retryAttemptRef = useRef(0);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    applyTheme(colorTheme);
  }, [colorTheme]);

  useEffect(() => {
    applyCardTextPx(cardTextPx);
  }, [cardTextPx]);

  const toggleColorTheme = useCallback(() => {
    setColorTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  const canDecreaseCardTextPx = cardTextPx > CARD_TEXT_PX_MIN;
  const canIncreaseCardTextPx = cardTextPx < CARD_TEXT_PX_MAX;

  const decreaseCardTextPx = useCallback(() => {
    setCardTextPx((current) => stepCardTextPx(current, -1));
  }, []);

  const increaseCardTextPx = useCallback(() => {
    setCardTextPx((current) => stepCardTextPx(current, 1));
  }, []);

  const handleCardTextPxChange = useCallback((event) => {
    setCardTextPx(Number(event.target.value));
  }, []);

  useEffect(() => {
    isSoundEnabledRef.current = isSoundEnabled;
    localStorage.setItem('kds_sound_enabled', String(isSoundEnabled));
  }, [isSoundEnabled]);

  const toggleSound = useCallback(() => {
    setIsSoundEnabled((enabled) => !enabled);
  }, []);

  useEffect(() => {
    showProductsRef.current = showProducts;
  }, [showProducts]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  useEffect(() => {
    const hasInteracted = localStorage.getItem('hasInteracted');
    if (hasInteracted === 'true') {
      setShowAudioPrompt(false);
      audioService.userInteracted = true;
      audioService.unlock();
    }
  }, []);

  const handleEnableAudio = () => {
    audioService.handleInteraction();
    setShowAudioPrompt(false);
    localStorage.setItem('hasInteracted', 'true');
  };

  const formatDateToMySQL = (date) => {
    const d = new Date(date);
    return d.toISOString().slice(0, 19).replace('T', ' ');
  };

  const TIMER_WARNING_MIN = 10;
  const TIMER_URGENT_MIN = 15;

  // The whole card carries the wait state: cooks read colour before they read the clock.
  // <10 light green, >=10 orange, >=15 red. Ready / cancelled override the wait palette.
  const getOrderTheme = (timer, { isReady, statut } = {}) => {
    if (statut === 'Annulee') {
      return {
        level: 'canceled',
        card: 'border-rose-200 bg-gradient-to-br from-rose-50 via-white to-rose-100/80 shadow-[0_10px_28px_rgba(244,63,94,0.12)] dark:border-rose-600/60 dark:from-neutral-950 dark:via-neutral-950 dark:to-rose-950/25 dark:shadow-[0_10px_28px_rgba(0,0,0,0.65)]',
        header: 'border-rose-100/80 bg-rose-50/50 dark:border-rose-900/50 dark:bg-rose-950/35',
        footer: 'border-rose-100/80 bg-white/50 dark:border-neutral-800 dark:bg-neutral-950/95',
        ticket: 'text-rose-700 dark:text-rose-400',
        chip: 'bg-white/80 text-rose-600 ring-1 ring-rose-200 dark:bg-neutral-900 dark:text-rose-400 dark:ring-rose-800/70',
        timer: 'bg-rose-500 text-white shadow-sm',
        iconWrap: 'bg-rose-100 text-rose-600',
        item: 'bg-white/70 ring-1 ring-rose-100/80 dark:bg-neutral-900 dark:text-neutral-100 dark:ring-rose-900/50',
        accent: 'from-rose-400 to-rose-500',
      };
    }

    if (isReady) {
      return {
        level: 'ready',
        card: 'border-sky-200 bg-gradient-to-br from-sky-50 via-white to-blue-50 shadow-[0_10px_28px_rgba(14,165,233,0.14)] dark:border-sky-600/60 dark:from-neutral-950 dark:via-neutral-950 dark:to-sky-950/25 dark:shadow-[0_10px_28px_rgba(0,0,0,0.65)]',
        header: 'border-sky-100/80 bg-sky-50/40 dark:border-sky-900/50 dark:bg-sky-950/35',
        footer: 'border-sky-100/80 bg-white/50 dark:border-neutral-800 dark:bg-neutral-950/95',
        ticket: 'text-sky-800 dark:text-sky-400',
        chip: 'bg-white/80 text-sky-700 ring-1 ring-sky-200 dark:bg-neutral-900 dark:text-sky-400 dark:ring-sky-800/70',
        timer: 'bg-sky-500 text-white shadow-sm',
        iconWrap: 'bg-sky-100 text-sky-600',
        item: 'bg-white/80 ring-1 ring-sky-100/80 dark:bg-neutral-900 dark:text-neutral-100 dark:ring-sky-900/50',
        accent: 'from-sky-400 to-blue-500',
      };
    }

    if (timer >= TIMER_URGENT_MIN) {
      return {
        level: 'urgent',
        card: 'border-red-300 bg-gradient-to-br from-red-50 via-rose-50 to-orange-50 shadow-[0_12px_32px_rgba(239,68,68,0.22)] dark:border-red-600/65 dark:from-neutral-950 dark:via-red-950/25 dark:to-neutral-950 dark:shadow-[0_12px_32px_rgba(0,0,0,0.7)]',
        header: 'border-red-100/80 bg-red-50/60 dark:border-red-900/50 dark:bg-red-950/40',
        footer: 'border-red-100/80 bg-white/40 dark:border-neutral-800 dark:bg-neutral-950/95',
        ticket: 'text-red-800 dark:text-red-400',
        chip: 'bg-white/80 text-red-700 ring-1 ring-red-200 dark:bg-neutral-900 dark:text-red-400 dark:ring-red-800/70',
        timer: 'bg-red-600 text-white shadow-md animate-kd-pulse-dot',
        iconWrap: 'bg-red-100 text-red-600',
        item: 'bg-white/75 ring-1 ring-red-100/80 dark:bg-neutral-900 dark:text-neutral-100 dark:ring-red-900/50',
        accent: 'from-red-500 to-rose-600',
      };
    }

    if (timer >= TIMER_WARNING_MIN) {
      return {
        level: 'warning',
        card: 'border-orange-300 bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 shadow-[0_12px_32px_rgba(249,115,22,0.18)] dark:border-orange-600/65 dark:from-neutral-950 dark:via-orange-950/25 dark:to-neutral-950 dark:shadow-[0_12px_32px_rgba(0,0,0,0.65)]',
        header: 'border-orange-100/80 bg-orange-50/50 dark:border-orange-900/50 dark:bg-orange-950/35',
        footer: 'border-orange-100/80 bg-white/40 dark:border-neutral-800 dark:bg-neutral-950/95',
        ticket: 'text-orange-900 dark:text-orange-400',
        chip: 'bg-white/80 text-orange-700 ring-1 ring-orange-200 dark:bg-neutral-900 dark:text-orange-400 dark:ring-orange-800/70',
        timer: 'bg-orange-500 text-white shadow-sm',
        iconWrap: 'bg-orange-100 text-orange-600',
        item: 'bg-white/75 ring-1 ring-orange-100/80 dark:bg-neutral-900 dark:text-neutral-100 dark:ring-orange-900/50',
        accent: 'from-orange-400 to-amber-500',
      };
    }

    return {
      level: 'fresh',
      card: 'border-emerald-200 bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50/70 shadow-[0_10px_28px_rgba(16,185,129,0.14)] dark:border-emerald-600/60 dark:from-neutral-950 dark:via-neutral-950 dark:to-emerald-950/25 dark:shadow-[0_10px_28px_rgba(0,0,0,0.65)]',
      header: 'border-emerald-100/80 bg-emerald-50/40 dark:border-emerald-900/50 dark:bg-emerald-950/35',
      footer: 'border-emerald-100/80 bg-white/40 dark:border-neutral-800 dark:bg-neutral-950/95',
      ticket: 'text-emerald-900 dark:text-emerald-400',
      chip: 'bg-white/80 text-emerald-700 ring-1 ring-emerald-200 dark:bg-neutral-900 dark:text-emerald-400 dark:ring-emerald-800/70',
      timer: 'bg-emerald-500 text-white shadow-sm',
      iconWrap: 'bg-emerald-100 text-emerald-600',
      item: 'bg-white/75 ring-1 ring-emerald-100/80 dark:bg-neutral-900 dark:text-neutral-100 dark:ring-emerald-900/50',
      accent: 'from-emerald-400 to-green-500',
    };
  };

  const parseFormuleItems = (formuleHtml) => {
    if (!formuleHtml) return [];
    return formuleHtml
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const calculateTimer = (dateCreation, isReady = false, orderTime = null) => {
    try {
      return getOrderAgeMinutes({ date_creation: dateCreation, order_time: orderTime });
    } catch (err) {
      console.error('Error calculating timer:', err, 'dateCreation:', dateCreation);
      return null;
    }
  };

  const fetchOrders = useCallback(
    // `isInitialOrToggle` drives the loading skeleton; `bypassCache` is separate so background
    // refreshes can pull fresh data without making the board flash a skeleton.
    async (readyOrders, isInitialOrToggle = false, bypassCache = false) => {
      try {
        if (isInitialOrToggle) setIsInitialOrToggleFetch(true);
        readyOrders ? setLoadingReady(true) : setLoadingInProgress(true);

        const id_caisse = localStorage.getItem('id_caisse');
        const id_account_caisse = localStorage.getItem('id_account_caisse');
        const current_day = getBusinessDay();

        const endpoint = readyOrders
          ? `${READY_ORDERS_ENDPOINT}/${encodeURIComponent(menuName)}`
          : `${ORDERS_ENDPOINT}/${encodeURIComponent(menuName)}`;

        const params = { id_caisse, id_account_caisse, current_day };
        const cacheKey = getCacheKey(menuName, endpoint, params);

        const cachedData = cache.get(cacheKey);
        if (cachedData && !isInitialOrToggle && !bypassCache) {
          console.log(`Cache hit for ${cacheKey}`);
          const ordersWithTimers = cachedData.map((order) => ({
            ...order,
            date_creation: order.date_creation,
            timer: calculateTimer(order.date_creation, order.isReady, order.order_time),
          }));
          readyOrders ? setReadyOrders(ordersWithTimers) : setInProgressOrders(ordersWithTimers);
          return ordersWithTimers;
        }

        console.log(`Cache miss for ${cacheKey}`);
        const response = await axios.get(endpoint, {
          params,
          headers: { 'Cache-Control': 'no-cache' },
        });

        const ordersWithTimers = (
          readyOrders ? response.data.readyorders : response.data.neworders || []
        ).map((order) => ({
          ...order,
          date_creation: order.date_creation,
          timer: calculateTimer(order.date_creation, order.isReady, order.order_time),
        }));

        cache.set(cacheKey, ordersWithTimers);
        readyOrders ? setReadyOrders(ordersWithTimers) : setInProgressOrders(ordersWithTimers);
        return ordersWithTimers;
      } catch (err) {
        console.error(`Error fetching ${readyOrders ? 'ready' : 'in-progress'} orders:`, err);
        // null rather than [], so callers can tell a failed fetch from a genuinely empty board.
        return null;
      } finally {
        readyOrders ? setLoadingReady(false) : setLoadingInProgress(false);
        if (isInitialOrToggle) setIsInitialOrToggleFetch(false);
      }
    },
    [menuName]
  );

  const fetchProducts = useCallback(
    async (bypassCache = false) => {
      try {
        setLoadingProducts(true);
        const id_caisse = localStorage.getItem('id_caisse');
        const id_account_caisse = localStorage.getItem('id_account_caisse');
        const current_day = getBusinessDay();

        const endpoint = `${PRODUCTS_ENDPOINT}/${encodeURIComponent(menuName)}`;
        const params = { id_caisse, id_account_caisse, current_day };
        const cacheKey = getCacheKey(menuName, endpoint, params);

        const cachedData = cache.get(cacheKey);
        if (cachedData && !bypassCache) {
          console.log(`Cache hit for ${cacheKey}`);
          setProducts(cachedData);
          return cachedData;
        }

        console.log(`Cache miss for ${cacheKey}`);
        const response = await axios.get(endpoint, {
          params,
          headers: { 'Cache-Control': 'no-cache' },
        });

        const productsData = response.data.products || [];
        cache.set(cacheKey, productsData);
        setProducts(productsData);
        return productsData;
      } catch (err) {
        // Keep the last known products on screen. Blanking the list on a transient error
        // looks identical to "nothing left to prepare", which is the one thing a cook must
        // never be told incorrectly. The freshness indicator flags that the data is stale.
        console.error('Error fetching products to prepare:', err);
        return null;
      } finally {
        setLoadingProducts(false);
      }
    },
    [menuName]
  );

  // A failed refresh must not leave the board waiting for the next scheduled poll, which could
  // be 30 seconds away. Retry quickly at first, then back off so a sustained outage does not
  // turn every display into a hammer on a struggling API.
  const scheduleRetry = useCallback(() => {
    if (retryTimerRef.current) return;

    retryAttemptRef.current += 1;
    const delay = Math.min(
      RETRY_BASE_DELAY_MS * 2 ** (retryAttemptRef.current - 1),
      RETRY_MAX_DELAY_MS
    );

    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      refreshAllRef.current();
    }, delay);
  }, []);

  useEffect(
    () => () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    },
    []
  );

  const fetchBothOrders = useCallback(
    async () => {
      try {
        setIsInitialOrToggleFetch(true);
        const results = await Promise.all([fetchOrders(false, true), fetchOrders(true, true)]);
        lastRefreshAtRef.current = Date.now();
        if (results.every((result) => result !== null)) {
          retryAttemptRef.current = 0;
          setLastUpdatedAt(Date.now());
        } else {
          // The very first load is exactly when a fast retry matters most.
          scheduleRetry();
        }
      } catch (err) {
        console.error('Error fetching both orders:', err);
      } finally {
        setIsInitialOrToggleFetch(false);
      }
    },
    [fetchOrders, scheduleRetry]
  );

  // Single source of truth for "pull the current board from the server". Overlapping callers are
  // coalesced into one pass plus one follow-up pass if anything arrived mid-fetch, so a burst of
  // events can neither pile up duplicate requests nor be dropped.
  const refreshAll = useCallback(async () => {
    if (refreshInFlightRef.current) {
      refreshQueuedRef.current = true;
      return;
    }

    refreshInFlightRef.current = true;
    try {
      do {
        refreshQueuedRef.current = false;
        clearOrderCaches();

        const results = await Promise.all([
          fetchOrders(false, false, true),
          fetchOrders(true, false, true),
        ]);
        if (showProductsRef.current) {
          results.push(await fetchProducts(true));
        }

        // The attempt timestamp always advances so the spacing guards keep working, but the
        // displayed freshness only advances when every request actually came back.
        lastRefreshAtRef.current = Date.now();
        if (results.every((result) => result !== null)) {
          retryAttemptRef.current = 0;
          setLastUpdatedAt(Date.now());
        } else {
          scheduleRetry();
        }
      } while (refreshQueuedRef.current);
    } catch (err) {
      console.error('Error refreshing kitchen display:', err);
      scheduleRetry();
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [fetchOrders, fetchProducts, scheduleRetry]);

  useEffect(() => {
    refreshAllRef.current = refreshAll;
  }, [refreshAll]);

  const debouncedRefreshAll = useMemo(() => debounce(() => refreshAllRef.current(), 400), []);

  const triggerBatchedFetch = useCallback(() => {
    setPendingFetches((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (pendingFetches > 0) {
      setPendingFetches(0);
      debouncedRefreshAll();
    }
  }, [pendingFetches, debouncedRefreshAll]);

  // The three boards are mutually exclusive, so they are driven by one selector rather
  // than two toggles whose labels flipped to name the state they were not in.
  const activeView = showProducts ? 'products' : showReadyOrders ? 'ready' : 'in-progress';

  const selectView = useCallback(
    async (view) => {
      if (view === activeView || isSwitching) return;

      // Tracked separately from isSwitching so the spinner appears on the tab that was
      // pressed, rather than on every tab that is not the current one.
      setPendingView(view);
      setIsSwitching(true);
      setFadeOut(true);

      try {
        if (view === 'products') {
          if ((await fetchProducts()) === null) {
            alert('Impossible de charger les produits. Veuillez réessayer.');
            return;
          }
          setShowProducts(true);
          setShowReadyOrders(false);
        } else {
          const wantReady = view === 'ready';
          if ((await fetchOrders(wantReady, true)) === null) {
            alert('Impossible de charger les commandes. Veuillez réessayer.');
            return;
          }
          setShowProducts(false);
          setShowReadyOrders(wantReady);
        }
      } finally {
        setFadeOut(false);
        setIsSwitching(false);
        setPendingView(null);
      }
    },
    [activeView, isSwitching, fetchOrders, fetchProducts]
  );

  const handleMarkAllReady = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir vider tous les tickets ?')) {
      return;
    }

    setIsMarkingAllReady(true);
    try {
      const id_caisse = localStorage.getItem('id_caisse');
      const id_account_caisse = localStorage.getItem('id_account_caisse');
      const current_day = getBusinessDay();

      await axios.post(`${API_BASE}/mark-all-tickets-ready`, {
        id_caisse,
        id_account_caisse,
        current_day,
        nom_imprimante: menuName,
      });

      clearOrderCaches();
      triggerBatchedFetch();
      setShowToast(true);
    } catch (err) {
      console.error('Error marking all tickets as ready:', err.response?.data || err.message);
      alert(`Failed to mark all tickets as ready: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsMarkingAllReady(false);
    }
  };

  useEffect(() => {
    fetchBothOrders();
  }, [fetchBothOrders]);

  // Mounted once for the lifetime of the screen: no dependency here may change, or the socket
  // gets recycled and events arriving mid-reconnect are lost.
  useEffect(() => {
    const id_caisse = localStorage.getItem('id_caisse');
    if (!id_caisse) {
      console.error('No id_caisse in storage: cannot subscribe to live order updates');
      setConnectionStatus('error');
      return undefined;
    }

    const pusher = new Pusher(PUSHER_KEY, {
      cluster: PUSHER_CLUSTER,
      forceTLS: true,
      // Tighter than the previous 60s/30s pair: a silently dead socket used to take up to 90
      // seconds to be noticed, and every event in that window was lost.
      activityTimeout: 30000,
      pongTimeout: 10000,
      unavailableTimeout: 10000,
      // enabledTransports is deliberately left unset: the default enables every transport,
      // including the HTTP fallbacks needed on restaurant networks that block raw WebSockets.
    });

    const channelName = `live_orders_accepter.${id_caisse}`;
    const channel = pusher.subscribe(channelName);

    const handleOrderEvent = async () => {
      setShowToast(true);

      const now = Date.now();
      if (isSoundEnabledRef.current && now - lastSoundAtRef.current > MIN_SOUND_SPACING_MS) {
        lastSoundAtRef.current = now;
        try {
          await audioService.playWithRetry();
        } catch (err) {
          console.error('Failed to play notification sound:', err);
        }
      }

      await refreshAllRef.current();
    };

    ORDER_EVENTS.forEach((event) => channel.bind(event, handleOrderEvent));

    const handleStateChange = (states) => {
      setConnectionStatus(states.current);
      // Anything broadcast while the socket was down is gone, so treat every (re)connection as a
      // point to resynchronise the board. The spacing check skips the very first connect, whose
      // data the initial load has already just fetched.
      if (
        states.current === 'connected' &&
        Date.now() - lastRefreshAtRef.current > MIN_REFRESH_SPACING_MS
      ) {
        refreshAllRef.current();
      }
    };

    const handleConnectionError = (err) => {
      console.error('Pusher connection error:', err);
    };

    const handleSubscriptionError = (err) => {
      console.error(`Pusher subscription to ${channelName} failed:`, err);
    };

    pusher.connection.bind('state_change', handleStateChange);
    pusher.connection.bind('error', handleConnectionError);
    channel.bind('pusher:subscription_error', handleSubscriptionError);

    return () => {
      ORDER_EVENTS.forEach((event) => channel.unbind(event, handleOrderEvent));
      channel.unbind('pusher:subscription_error', handleSubscriptionError);
      pusher.connection.unbind('state_change', handleStateChange);
      pusher.connection.unbind('error', handleConnectionError);
      pusher.unsubscribe(channelName);
      pusher.disconnect();
    };
  }, []);

  // Safety net so the board can never sit stale waiting for a push that will not arrive.
  // Self-rescheduling rather than setInterval, so each tick can carry its own jitter.
  useEffect(() => {
    const base =
      connectionStatus === 'connected' ? POLL_INTERVAL_HEALTHY_MS : POLL_INTERVAL_DEGRADED_MS;
    let timer = null;

    const scheduleNextPoll = () => {
      timer = setTimeout(() => {
        const skip =
          document.visibilityState === 'hidden' ||
          Date.now() - lastRefreshAtRef.current < MIN_REFRESH_SPACING_MS;

        if (!skip) {
          refreshAllRef.current();
        }
        scheduleNextPoll();
      }, base + Math.random() * POLL_JITTER_MS);
    };

    scheduleNextPoll();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [connectionStatus]);

  // Browsers throttle timers and drop sockets in background tabs, and these screens are often
  // left unattended, so recover as soon as the page is looked at or the network returns.
  useEffect(() => {
    // `focus` in particular fires on every click back into the window, so rate limit the resync.
    const resync = () => {
      if (Date.now() - lastRefreshAtRef.current < MIN_REFRESH_SPACING_MS) return;
      refreshAllRef.current();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resync();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', resync);
    window.addEventListener('online', resync);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', resync);
      window.removeEventListener('online', resync);
    };
  }, []);

  // A kitchen screen is watched, not touched, so the device's idle timer eventually dims or
  // locks it and orders go unseen until someone taps it. Hold a wake lock for as long as the
  // board is visible. The API is unavailable on older browsers and over plain HTTP, and the
  // lock is dropped by the system whenever the page is hidden, so both cases are handled by
  // simply reacquiring on visibility and ignoring failures.
  useEffect(() => {
    if (!('wakeLock' in navigator)) return undefined;

    let sentinel = null;
    let cancelled = false;

    const requestWakeLock = async () => {
      if (cancelled || document.visibilityState !== 'visible' || sentinel) return;
      try {
        sentinel = await navigator.wakeLock.request('screen');
        sentinel.addEventListener('release', () => {
          sentinel = null;
        });
      } catch (err) {
        console.warn('Screen wake lock unavailable:', err.message);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    };

    requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      if (sentinel) sentinel.release().catch(() => {});
    };
  }, []);

  useEffect(() => {
    const updateTimers = () => {
      setInProgressOrders((prev) =>
        prev.map((order) => ({
          ...order,
          timer: calculateTimer(order.date_creation, order.isReady, order.order_time),
        }))
      );
      setReadyOrders((prev) =>
        prev.map((order) => ({
          ...order,
          timer: calculateTimer(order.date_creation, order.isReady, order.order_time),
        }))
      );
    };

    updateTimers();
    const interval = setInterval(updateTimers, 45000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!selectedOrder) return;

    setSelectedOrderTimer(
      calculateTimer(selectedOrder.date_creation, selectedOrder.isReady, selectedOrder.order_time)
    );
    const interval = setInterval(() => {
      setSelectedOrderTimer(
        calculateTimer(selectedOrder.date_creation, selectedOrder.isReady, selectedOrder.order_time)
      );
    }, 45000);

    return () => clearInterval(interval);
  }, [selectedOrder]);

  const handleProductStatUpdate = async (order, productIndex) => {
    const id_ticket_cle = order.id_ticket_cle[productIndex];
    const product = order.products[productIndex];
    const newStat = product.stat === 1 ? 0 : 1;

    setButtonLoading((prev) => ({ ...prev, [`product-${id_ticket_cle}-${productIndex}`]: true }));

    try {
      const id_caisse = order.id_caisse?.toString() || localStorage.getItem('id_caisse');
      const id_account_caisse = localStorage.getItem('id_account_caisse');

      if (!id_ticket_cle || !id_caisse || !id_account_caisse) {
        console.error('Missing required fields:', {
          id_ticket_cle,
          id_caisse,
          id_account_caisse,
        });
        throw new Error('Missing required fields for stat update');
      }

      await axios.post(`${API_BASE}/update-ticket-stat`, {
        id_ticket_cle,
        id_caisse,
        id_account_caisse,
        stat: newStat,
      });

      const updateOrders = (orders) =>
        orders.map((o) =>
          o.id_ticket_cle.includes(id_ticket_cle)
            ? {
                ...o,
                products: o.products.map((p, idx) =>
                  idx === productIndex ? { ...p, stat: newStat } : p
                ),
              }
            : o
        );

      // Functional form: a background refresh may have replaced these lists since render.
      setInProgressOrders(updateOrders);
      setReadyOrders(updateOrders);

      if (selectedOrder && selectedOrder.id_ticket_cle.includes(id_ticket_cle)) {
        setSelectedOrder((prev) => ({
          ...prev,
          products: prev.products.map((p, idx) =>
            idx === productIndex ? { ...p, stat: newStat } : p
          ),
        }));
      }

      clearOrderCaches();
      triggerBatchedFetch();
    } catch (err) {
      console.error('Error updating product stat:', err.response?.data || err.message);
      alert('Failed to update product status. Please try again.');
    } finally {
      setButtonLoading((prev) => ({ ...prev, [`product-${id_ticket_cle}-${productIndex}`]: false }));
    }
  };

  const handleDone = useCallback(async (uniqueOrderId, order) => {
    setButtonLoading((prev) => ({ ...prev, [uniqueOrderId]: true }));
    try {
      const id_caisse = order.id_caisse?.toString() || localStorage.getItem('id_caisse');
      const id_account_caisse = localStorage.getItem('id_account_caisse');

      if (!id_caisse || !id_account_caisse || !order.id_ticket_cle) {
        console.error('Missing required fields for update-ticket:', {
          id_caisse,
          id_account_caisse,
          id_ticket_cle: order.id_ticket_cle,
        });
        throw new Error('Missing required fields: id_caisse, id_account_caisse, or id_ticket_cle');
      }

      const idTicketStr = order.id_ticket_cle.join(',');
      const statutArray = {
        [idTicketStr]: order.products.map((p) => p.statut_ticket || 'Valide'),
      };

      await axios.post(`${API_BASE}/update-ticket`, {
        id_ticket_cle: [order.id_ticket_cle],
        id_caisse,
        date_creation: formatDateToMySQL(order.date_creation),
        statut_array: statutArray,
        id_account_caisse,
        nom_imprimante: menuName,
      });

      setInProgressOrders((prev) =>
        prev.filter((o) => o.id_ticket_cle.join(',') !== uniqueOrderId)
      );

      // Functional form so the detail panel is closed based on what is open now, not on
      // whatever was open when this card was last rendered.
      setSelectedOrder((current) =>
        current && current.id_ticket_cle.join(',') === uniqueOrderId ? null : current
      );

      clearOrderCaches();
      triggerBatchedFetch();
    } catch (err) {
      console.error('Error marking order as done:', err.response?.data || err.message);
      alert(`Failed to mark order as done: ${err.response?.data?.error || err.message}`);
      triggerBatchedFetch();
    } finally {
      setButtonLoading((prev) => ({ ...prev, [uniqueOrderId]: false }));
    }
  }, [menuName, triggerBatchedFetch]);

  const handleBack = useCallback(async (uniqueOrderId, order) => {
    setButtonLoading((prev) => ({ ...prev, [uniqueOrderId]: true }));
    try {
      const id_caisse = order.id_caisse?.toString() || localStorage.getItem('id_caisse');
      const id_account_caisse = localStorage.getItem('id_account_caisse');

      if (!id_caisse || !id_account_caisse || !order.id_ticket_cle) {
        console.error('Missing required fields for revert-ticket:', {
          id_caisse,
          id_account_caisse,
          id_ticket_cle: order.id_ticket_cle,
        });
        throw new Error('Missing required fields: id_caisse, id_account_caisse, or id_ticket_cle');
      }

      await axios.post(`${API_BASE}/revert-ticket`, {
        id_ticket_cle: [order.id_ticket_cle],
        id_caisse,
        id_account_caisse,
        // Without this the revert clears the ticket's readiness on every preparation
        // point, not just this screen's.
        nom_imprimante: menuName,
      });

      setReadyOrders((prev) =>
        prev.filter((o) => o.id_ticket_cle.join(',') !== uniqueOrderId)
      );

      setSelectedOrder((current) =>
        current && current.id_ticket_cle.join(',') === uniqueOrderId ? null : current
      );

      clearOrderCaches();
      triggerBatchedFetch();
    } catch (err) {
      console.error('Error reverting order:', err.response?.data || err.message);
      alert(`Failed to revert order: ${err.response?.data?.error || err.message}`);
      triggerBatchedFetch();
    } finally {
      setButtonLoading((prev) => ({ ...prev, [uniqueOrderId]: false }));
    }
  }, [menuName, triggerBatchedFetch]);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.() ||
        document.documentElement.webkitRequestFullscreen?.() ||
        document.documentElement.msRequestFullscreen?.();
    } else {
      document.exitFullscreen?.() ||
        document.webkitExitFullscreen?.() ||
        document.msExitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handler = () => {
      setIsFullscreen(
        !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.msFullscreenElement
        )
      );
    };

    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    document.addEventListener('msfullscreenchange', handler);

    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
      document.removeEventListener('msfullscreenchange', handler);
    };
  }, []);

  const handleCardClick = (order) => {
    const uniqueOrderId = order.id_ticket_cle.join(',');
    if ((order.previous_products || []).length > 0) {
      setExpandedHistoryIds((prev) => ({ ...prev, [uniqueOrderId]: true }));
    }
    setSelectedOrder(order);
  };
  const closePopup = () => setSelectedOrder(null);

  const toggleHistory = (uniqueOrderId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setExpandedHistoryIds((prev) => ({
      ...prev,
      [uniqueOrderId]: !prev[uniqueOrderId],
    }));
  };

  const formatPreparedAt = (preparedAt) => {
    if (!preparedAt) return null;
    try {
      const date = new Date(preparedAt.endsWith('Z') ? preparedAt : `${preparedAt}Z`);
      if (Number.isNaN(date.getTime())) return null;
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      });
    } catch {
      return null;
    }
  };

  const formatTicketTime = (order) => {
    try {
      const date = getCanonicalOrderDate(order);
      if (!date) return null;
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      });
    } catch {
      return null;
    }
  };

  const renderPreviousProducts = (order, uniqueOrderId, { alwaysExpanded = false } = {}) => {
    const previous = order.previous_products || [];
    if (previous.length === 0 || order.isReady) return null;

    const expanded = alwaysExpanded || Boolean(expandedHistoryIds[uniqueOrderId]);

    return (
      <div>
        {!alwaysExpanded ? (
          <button
            type="button"
            className="kd-card-chip-sm inline-flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
            onClick={(e) => toggleHistory(uniqueOrderId, e)}
            aria-expanded={expanded}
          >
            <span>
              {expanded ? 'Masquer préparés' : 'Voir préparés'} ({previous.length})
            </span>
            {expanded ? (
              <ChevronUp className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden="true" />
            ) : (
              <ChevronDown className="h-4 w-4 shrink-0" strokeWidth={2.25} aria-hidden="true" />
            )}
          </button>
        ) : (
          <p className="kd-card-chip-xs mb-2 font-bold uppercase tracking-wide text-slate-500 dark:text-neutral-300">
            Produits déjà préparés ({previous.length})
          </p>
        )}
        {expanded && (
          <ul className={cx('space-y-2', !alwaysExpanded && 'mt-2')}>
            {previous.map((product, idx) => {
              const formuleItems = parseFormuleItems(product.formule);
              const canceled = isCanceledProduct(product);
              const preparedTime = formatPreparedAt(product.prepared_at);
              return (
                <li
                  key={`prev-${idx}`}
                  className="kd-card-product rounded-xl bg-emerald-50/45 px-3 py-2.5 ring-1 ring-emerald-100/80 dark:bg-emerald-950/25 dark:ring-emerald-900/40"
                >
                  <div className="mb-1.5 flex flex-wrap items-center gap-2">
                    <span className="kd-card-chip-xs inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 uppercase tracking-wide text-emerald-600/90">
                      Préparé
                      {preparedTime ? (
                        <span className="normal-case tracking-normal tabular-nums font-semibold">· {preparedTime}</span>
                      ) : null}
                    </span>
                  </div>
                  <span
                    className={cx(
                      'font-medium kd-struck-line',
                      canceled ? 'kd-canceled-line' : 'text-slate-400 dark:text-neutral-400'
                    )}
                    style={canceled ? CANCELED_STRIKE_STYLE : PREPARED_STRIKE_STYLE}
                  >
                    <span className="kd-card-product-qty mr-1 inline-flex min-w-[1.5rem] justify-center rounded-md bg-emerald-100/45 px-1.5 py-0.5 text-emerald-600/75">
                      {product.total_quantity}
                    </span>
                    {product.designation}
                  </span>
                  {product.note && (
                    <span
                      className="kd-card-product-note mt-1.5 flex items-start gap-1.5 font-medium text-slate-400/90 kd-struck-line dark:text-neutral-400"
                      style={PREPARED_STRIKE_STYLE}
                    >
                      <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2.25} aria-hidden="true" />
                      {product.note}
                    </span>
                  )}
                  {formuleItems.length > 0 && (
                    <div className="kd-card-product-formule mt-1.5 space-y-0.5 border-l-2 border-emerald-100 pl-2.5 font-medium text-slate-400/80 kd-struck-line dark:border-emerald-900/50 dark:text-neutral-400">
                      {formuleItems.map((item, formuleIdx) => (
                        <div key={formuleIdx}>{item}</div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  const handleLogout = () => {
    clearSession();
    navigate('/');
  };
  const handleBackToMenus = () => navigate('/menu-selection');

  const renderCurrentProductItems = (order, theme) =>
    order.products.map((product, idx) => {
      const formuleItems = parseFormuleItems(product.formule);
      const canceled = isCanceledProduct(product);
      const prepared = Number(product.stat) === 1 && !canceled;
      return (
        <li
          key={idx}
          className={cx(
            'kd-card-product rounded-xl shadow-sm',
            theme.item,
            canceled && 'bg-rose-50 ring-1 ring-rose-200',
            prepared && 'bg-emerald-50/35 ring-1 ring-emerald-100/70'
          )}
        >
          <span
            className={cx(
              'font-semibold',
            canceled ? 'kd-canceled-line' : 'text-slate-800 dark:text-white',
            prepared && 'kd-struck-line font-medium text-slate-400 dark:text-neutral-400'
            )}
            style={canceled ? CANCELED_STRIKE_STYLE : prepared ? PREPARED_STRIKE_STYLE : undefined}
          >
            <span
              className={cx(
                'kd-card-product-qty mr-1 inline-flex min-w-[1.5rem] justify-center rounded-md px-1.5 py-0.5',
                canceled ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300' : 'bg-slate-900/5 text-slate-700 dark:bg-neutral-800 dark:text-neutral-100'
              )}
            >
              {product.total_quantity}
            </span>
            {product.designation}
          </span>
          {product.note && (
            <span
              className={cx(
                'kd-card-product-note mt-1.5 flex items-start gap-1.5 font-semibold',
                canceled ? 'kd-canceled-line' : 'text-orange-600 dark:text-orange-300'
              )}
              style={canceled ? CANCELED_STRIKE_STYLE : undefined}
            >
              <StickyNote className="kd-card-icon-sm mt-0.5 shrink-0" strokeWidth={2.25} aria-hidden="true" />
              {product.note}
            </span>
          )}
          {formuleItems.length > 0 && (
            <div
              className={cx(
                'kd-card-product-formule mt-1.5 space-y-0.5 border-l-2 pl-2.5 font-medium',
                canceled ? 'kd-canceled-line border-red-300 dark:border-red-800' : 'border-slate-200 text-slate-500 dark:border-neutral-700 dark:text-neutral-300'
              )}
              style={canceled ? CANCELED_STRIKE_STYLE : undefined}
            >
              {formuleItems.map((item, formuleIdx) => (
                <div key={formuleIdx}>{item}</div>
              ))}
            </div>
          )}
        </li>
      );
    });

  const renderedOrders = useMemo(() => {
    const ordersToRender = showReadyOrders ? readyOrders : inProgressOrders;

    return ordersToRender.map((order, index) => {
      const uniqueOrderId = order.id_ticket_cle.join(',');
      const busy = Boolean(buttonLoading[uniqueOrderId]);
      const theme = getOrderTheme(order.timer, order);
      const ticketTime = formatTicketTime(order);
      const historyExpanded = Boolean(expandedHistoryIds[uniqueOrderId]);
      const hasPrevious = (order.previous_products || []).length > 0 && !order.isReady;
      const productAreaCompact = !historyExpanded;

      return (
        <article
          key={`${uniqueOrderId}-${index}`}
          className={cx(
            'group relative flex max-w-full cursor-pointer flex-col rounded-[20px] border transition duration-200 hover:-translate-y-1 hover:shadow-xl',
            historyExpanded
              ? 'kd-order-card--expanded h-auto overflow-visible'
              : cx('overflow-hidden', ORDER_CARD_HEIGHT_CLASS),
            'w-full min-w-0',
            theme.card
          )}
          onClick={() => handleCardClick(order)}
        >
          <div className={cx('h-1.5 w-full shrink-0 bg-gradient-to-r', theme.accent)} />

          <OrderCardHeader
            order={order}
            theme={theme}
            ticketTime={ticketTime}
            timerMinutes={order.timer}
            className="kd-card-pad-header shrink-0"
          />

          <div
            className={cx(
              'kd-card-pad-body flex flex-col',
              historyExpanded ? 'shrink-0 pb-2' : 'kd-order-card-body min-h-0 flex-1 overflow-hidden'
            )}
          >
            <div className="flex shrink-0 items-center justify-between gap-2">
              <h3 className="kd-card-title min-w-0 truncate text-slate-800 dark:text-white">
                {order.nom_ticket}
              </h3>
              {order.products.length > ORDER_CARD_VISIBLE_PRODUCTS ? (
                <span className="kd-card-chip-xs shrink-0 rounded-md bg-slate-900/5 px-1.5 py-0.5 tabular-nums text-slate-500 dark:bg-neutral-800/50 dark:text-neutral-300">
                  {order.products.length} art.
                </span>
              ) : null}
            </div>

            {historyExpanded ? (
              <>
                <ul className="kd-card-product-list">{renderCurrentProductItems(order, theme)}</ul>
                {hasPrevious ? (
                  <div
                    className="mt-3 border-t border-black/[0.08] pt-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderPreviousProducts(order, uniqueOrderId)}
                  </div>
                ) : null}
              </>
            ) : (
              <OrderCardProductsScroll
                scrollNeeded={productAreaCompact}
                layoutKey={cardTextPx}
                className={cx(productAreaCompact && ORDER_CARD_PRODUCTS_SCROLL_CLASS)}
              >
                <ul className="kd-card-product-list">{renderCurrentProductItems(order, theme)}</ul>
              </OrderCardProductsScroll>
            )}
          </div>

          <div className={cx('relative z-10 w-full shrink-0', !historyExpanded && 'mt-auto')}>
            {!historyExpanded && hasPrevious ? (
              <div
                className="relative z-10 border-t border-black/[0.08] bg-white/95 px-3 py-2 shadow-[0_-6px_16px_rgba(15,23,42,0.06)] dark:border-white/[0.06] dark:bg-neutral-950/95 dark:shadow-[0_-6px_16px_rgba(0,0,0,0.35)] sm:px-4"
                onClick={(e) => e.stopPropagation()}
              >
                {renderPreviousProducts(order, uniqueOrderId)}
              </div>
            ) : null}

            <OrderCardFooter
              order={order}
              theme={theme}
              busy={busy}
              onReady={(e) => {
                e.stopPropagation();
                handleDone(uniqueOrderId, order);
              }}
              onBack={(e) => {
                e.stopPropagation();
                handleBack(uniqueOrderId, order);
              }}
            />
          </div>
        </article>
      );
    });
  }, [
    inProgressOrders,
    readyOrders,
    showReadyOrders,
    buttonLoading,
    handleDone,
    handleBack,
    expandedHistoryIds,
    cardTextPx,
  ]);

  const renderedProducts = useMemo(() => {
    return products.map((product, index) => (
      <article
        key={`${product.id_prix}-${index}`}
        className="kd-product-card flex w-full min-w-0 flex-col justify-between rounded-2xl border border-kd-line border-l-4 border-l-kd-warn bg-gradient-to-br from-white to-orange-50/50 p-4 shadow-card dark:border-neutral-800 dark:border-l-orange-500 dark:from-neutral-950 dark:to-neutral-900 dark:shadow-[0_8px_24px_rgba(0,0,0,0.45)] sm:p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <h3 className="kd-card-product-card-title text-kd-ink dark:text-white">{product.designation}</h3>
          <span className="kd-card-product-card-qty shrink-0 rounded-full bg-kd-warn px-3 py-1 text-white">
            {product.total_quantity} unité(s)
          </span>
        </div>
        <p className="kd-card-product-card-meta mt-4 text-kd-soft dark:text-neutral-300">
          <span className="font-semibold text-kd-ink dark:text-white">Tickets associés:</span>{' '}
          #{(product.ticket_cle_list || []).join(', #')}
        </p>
      </article>
    ));
  }, [products]);

  const connectionTone =
    connectionStatus === 'connected'
      ? 'bg-emerald-50 text-kd-ok dark:bg-emerald-950/40 dark:text-emerald-400'
      : connectionStatus === 'connecting'
        ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
        : 'bg-red-50 text-kd-danger dark:bg-red-950/40 dark:text-red-400';

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-slate-100 via-slate-50 to-slate-200 pb-[calc(5rem+env(safe-area-inset-bottom))] font-sans text-kd-ink transition-colors duration-200 dark:from-black dark:via-neutral-950 dark:to-black dark:text-white [padding-left:env(safe-area-inset-left)] [padding-right:env(safe-area-inset-right)]">
      {showAudioPrompt && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm dark:bg-black/70">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-toast dark:border dark:border-neutral-800 dark:bg-neutral-950">
            <h3 className="text-xl font-bold text-kd-ink dark:text-white">Activer les Notifications</h3>
            <p className="mt-2 text-sm leading-relaxed text-kd-soft dark:text-neutral-300">
              Cliquez pour activer les sons de notification pour les nouvelles commandes.
            </p>
            <button
              type="button"
              onClick={handleEnableAudio}
              className="mt-5 inline-flex h-11 items-center justify-center rounded-xl bg-kd-ok px-6 text-sm font-bold text-white transition hover:bg-kd-ok-dark"
            >
              Activer
            </button>
          </div>
        </div>
      )}

      <header className="kd-app-header sticky top-0 z-[100] border-b border-kd-line/80 bg-white/90 pt-[env(safe-area-inset-top)] shadow-header backdrop-blur-xl dark:border-neutral-800 dark:bg-black/95">
        <div className="kd-app-header-inner">
          {/* Row 1 — station title + primary nav */}
          <div className="kd-app-header-brand">
            <img
              src="/lacaisse_logo.svg"
              alt="La Caisse"
              className="kd-app-header-logo"
            />
            <div className="min-w-0 flex-1">
              <h2
                className="truncate text-[13px] font-extrabold tracking-tight text-kd-ink dark:text-white sm:text-sm md:text-[15px] lg:text-base"
                title={menuName}
              >
                {menuName}
              </h2>
              <div className="mt-0 flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-kd-soft dark:text-neutral-300 sm:text-[10px]">
                <span className={cx('h-1.5 w-1.5 shrink-0 rounded-full', VIEW_DOT[activeView])} />
                <span className="truncate">{VIEW_STATUS[activeView]}</span>
              </div>
            </div>
            <button
              type="button"
              className="kd-icon-btn kd-icon-btn--compact shrink-0 border-kd-line bg-kd-wash text-kd-ink hover:bg-white dark:border-neutral-700 dark:bg-neutral-900 dark:text-white dark:hover:bg-neutral-800"
              onClick={handleBackToMenus}
              title="Points de préparation"
              aria-label="Points de préparation"
            >
              <LayoutGrid aria-hidden="true" />
              <span className="hidden lg:inline">Points</span>
            </button>
          </div>

          {/* Row 2 (mobile) / center (desktop) — view switcher */}
          <div className="kd-app-header-views">
            <nav
              className="kd-app-header-tabs"
              role="tablist"
              aria-label="Vue des commandes"
            >
              {ORDER_VIEWS.map(({ id, label, Icon }) => {
                const selected = activeView === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    className={cx(
                      'kd-app-header-tab',
                      selected
                        ? 'bg-white text-kd-brand shadow-sm dark:bg-neutral-800 dark:text-blue-400'
                        : 'text-kd-soft hover:bg-white/70 hover:text-kd-ink dark:text-neutral-300 dark:hover:bg-neutral-800/70 dark:hover:text-white',
                      isSwitching && !selected && 'opacity-55'
                    )}
                    onClick={() => selectView(id)}
                    disabled={isSwitching}
                  >
                    {pendingView === id ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-kd-spin sm:h-[18px] sm:w-[18px]" aria-hidden="true" />
                    ) : (
                      <Icon className="h-4 w-4 shrink-0 sm:h-[18px] sm:w-[18px]" aria-hidden="true" />
                    )}
                    <span className="truncate">{label}</span>
                  </button>
                );
              })}
            </nav>

            <button
              type="button"
              className={cx(
                'kd-app-header-products',
                activeView === 'products'
                  ? 'border-kd-warn bg-kd-warn text-white shadow-sm'
                  : 'border-orange-200 bg-orange-50 text-kd-warn hover:bg-orange-100 dark:border-orange-800/60 dark:bg-orange-950/30 dark:text-orange-400 dark:hover:bg-orange-950/50',
                isSwitching && activeView !== 'products' && 'opacity-55'
              )}
              onClick={() => selectView('products')}
              disabled={isSwitching}
              aria-pressed={activeView === 'products'}
              aria-label="Produits"
              title="Produits"
            >
              {pendingView === 'products' ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-kd-spin sm:h-[18px] sm:w-[18px]" aria-hidden="true" />
              ) : (
                <Boxes className="h-4 w-4 shrink-0 sm:h-[18px] sm:w-[18px]" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Row 3 (mobile) / right (desktop) — tools */}
          <div className="kd-app-header-tools">
            <div
              className="kd-app-header-text-scale"
              role="group"
              aria-label="Taille du texte des cartes en pixels"
            >
              <span className="kd-app-header-text-label">
                <Type className="h-3.5 w-3.5" aria-hidden="true" />
                Texte
              </span>
              <button
                type="button"
                className="kd-text-scale-btn"
                onClick={decreaseCardTextPx}
                disabled={!canDecreaseCardTextPx}
                title="Réduire le texte des cartes"
                aria-label="Réduire le texte des cartes"
              >
                <Minus aria-hidden="true" />
              </button>
              <input
                type="range"
                className="kd-text-scale-range"
                min={CARD_TEXT_PX_MIN}
                max={CARD_TEXT_PX_MAX}
                step={1}
                value={cardTextPx}
                onChange={handleCardTextPxChange}
                aria-label="Taille du texte des cartes"
                aria-valuemin={CARD_TEXT_PX_MIN}
                aria-valuemax={CARD_TEXT_PX_MAX}
                aria-valuenow={cardTextPx}
                aria-valuetext={`${cardTextPx} pixels`}
              />
              <span
                className="kd-app-header-text-value"
                title={`Taille de base des cartes : ${cardTextPx}px`}
              >
                {cardTextPx}px
              </span>
              <button
                type="button"
                className="kd-text-scale-btn"
                onClick={increaseCardTextPx}
                disabled={!canIncreaseCardTextPx}
                title="Agrandir le texte des cartes"
                aria-label="Agrandir le texte des cartes"
              >
                <Plus aria-hidden="true" />
              </button>
            </div>

            <div className="kd-app-header-actions">
              <button
                type="button"
                className={cx(
                  'kd-icon-btn kd-icon-btn--compact',
                  colorTheme === 'dark'
                    ? 'border-amber-500/60 bg-amber-500/15 text-amber-300 hover:bg-amber-500/25'
                    : 'border-slate-300 bg-slate-100 text-slate-700 hover:bg-white dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200'
                )}
                onClick={toggleColorTheme}
                title={colorTheme === 'dark' ? 'Mode clair' : 'Mode sombre'}
                aria-label={colorTheme === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'}
                aria-pressed={colorTheme === 'dark'}
              >
                {colorTheme === 'dark' ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
              </button>

              <button
                type="button"
                className={cx(
                  'kd-icon-btn kd-icon-btn--compact',
                  isSoundEnabled
                    ? 'border-emerald-600 bg-kd-ok text-white hover:border-emerald-700 hover:bg-kd-ok-dark'
                    : 'border-kd-danger bg-kd-danger text-white hover:border-kd-danger-dark hover:bg-kd-danger-dark'
                )}
                onClick={toggleSound}
                title={isSoundEnabled ? 'Couper le son' : 'Activer le son'}
                aria-label={isSoundEnabled ? 'Couper le son' : 'Activer le son'}
                aria-pressed={!isSoundEnabled}
              >
                {isSoundEnabled ? <Volume2 aria-hidden="true" /> : <VolumeX aria-hidden="true" />}
              </button>

              <button
                type="button"
                className="kd-icon-btn kd-icon-btn--compact"
                onClick={toggleFullscreen}
                title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
                aria-label={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
              >
                {isFullscreen ? <Minimize2 aria-hidden="true" /> : <Maximize2 aria-hidden="true" />}
              </button>

              <span className="kd-app-header-divider" aria-hidden="true" />

              <button
                type="button"
                className="kd-icon-btn kd-icon-btn--compact border-kd-danger bg-kd-danger text-white hover:border-kd-danger-dark hover:bg-kd-danger-dark"
                onClick={handleMarkAllReady}
                disabled={isMarkingAllReady}
                title="Vider tous les tickets"
                aria-label="Vider tous les tickets"
              >
                {isMarkingAllReady ? (
                  <Loader2 className="animate-kd-spin" aria-hidden="true" />
                ) : (
                  <Trash2 aria-hidden="true" />
                )}
              </button>

              <button
                type="button"
                className="kd-icon-btn kd-icon-btn--compact border-slate-800 bg-slate-800 text-white hover:border-slate-900 hover:bg-slate-900"
                onClick={handleLogout}
                title="Déconnexion"
                aria-label="Déconnexion"
              >
                <LogOut aria-hidden="true" />
                <span className="hidden xl:inline">Quitter</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {showToast && (
        <div className="fixed right-4 top-20 z-[1100] flex items-center gap-2 rounded-xl bg-kd-ok px-4 py-3 text-sm font-semibold text-white shadow-toast animate-kd-fade-in sm:right-6">
          <span aria-hidden="true">🔔</span>
          <span>Nouvelle commande reçue !</span>
        </div>
      )}

      <main className="w-full flex-1 py-2 sm:py-3">
        {(showProducts ? loadingProducts : showReadyOrders ? loadingReady : loadingInProgress) &&
        isInitialOrToggleFetch ? (
          <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-kd-soft dark:text-neutral-300">
            <div className="h-10 w-10 rounded-full border-4 border-kd-line border-t-kd-brand animate-kd-spin dark:border-neutral-800 dark:border-t-white" />
            <p className="text-sm font-medium">
              {showProducts ? 'Chargement des produits...' : 'Chargement des commandes...'}
            </p>
          </div>
        ) : (
          <div
            className={cx(ORDER_CARD_GRID_CLASS, fadeOut ? 'opacity-40' : 'animate-kd-fade-in opacity-100')}
          >
            {showProducts ? (
              products.length === 0 ? (
                <p className="col-span-full py-16 text-center text-base font-medium text-kd-soft dark:text-neutral-300">
                  Aucun produit à préparer...
                </p>
              ) : (
                renderedProducts
              )
            ) : (showReadyOrders ? readyOrders : inProgressOrders).length === 0 ? (
              <p className="col-span-full py-16 text-center text-base font-medium text-kd-soft dark:text-neutral-300">
                Aucune commande disponible...
              </p>
            ) : (
              renderedOrders
            )}
          </div>
        )}
      </main>

      {isSwitching && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-white/75 backdrop-blur-[2px] dark:bg-black/60">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white px-8 py-6 shadow-card dark:border dark:border-neutral-800 dark:bg-neutral-950">
            <div className="h-10 w-10 rounded-full border-4 border-kd-line border-t-kd-brand animate-kd-spin dark:border-neutral-800 dark:border-t-white" />
            <p className="text-sm font-medium text-kd-soft dark:text-neutral-300">
              {showProducts ? 'Chargement des produits...' : 'Chargement des commandes...'}
            </p>
          </div>
        </div>
      )}

      {selectedOrder && (() => {
        const popupTheme = getOrderTheme(selectedOrderTimer, selectedOrder);
        const ticketTime = formatTicketTime(selectedOrder);
        return (
        <div className="fixed inset-0 z-[1000] flex items-end justify-center bg-slate-900/60 p-3 backdrop-blur-sm dark:bg-black/75 sm:items-center sm:p-6">
          <div
            className={cx(
              'kd-order-popup relative flex max-h-[min(92vh,900px)] w-full max-w-lg flex-col overflow-hidden rounded-[24px] border shadow-toast sm:max-w-xl md:max-w-2xl',
              popupTheme.card
            )}
          >
            <div className={cx('h-1.5 w-full bg-gradient-to-r', popupTheme.accent)} />
            <button
              type="button"
              className="absolute right-3 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow-sm ring-1 ring-black/5 transition hover:bg-white hover:text-slate-800 dark:bg-neutral-900 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-white"
              onClick={closePopup}
              aria-label="Fermer"
            >
              <X className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
            </button>

            <OrderCardHeader
              order={selectedOrder}
              theme={popupTheme}
              ticketTime={ticketTime}
              timerMinutes={selectedOrderTimer}
              className="px-5 pb-3.5 pt-5 pr-14"
            />

            <div className="flex-1 overflow-y-auto px-5 py-4">
              <h3 className="kd-card-title mb-3 text-slate-800 dark:text-white">{selectedOrder.nom_ticket}</h3>
              <ul className="space-y-2">
                {selectedOrder.products.map((product, index) => {
                  const formuleItems = parseFormuleItems(product.formule);
                  const canceled = isCanceledProduct(product);
                  const prepared = Number(product.stat) === 1 && !canceled;
                  return (
                    <li
                      key={index}
                      className={cx(
                        'kd-card-product relative rounded-xl shadow-sm',
                        popupTheme.item,
                        canceled && 'bg-rose-50 ring-1 ring-rose-200',
                        prepared && 'bg-emerald-50/35 ring-1 ring-emerald-100/70',
                        !canceled && 'cursor-pointer hover:brightness-[0.98]'
                      )}
                      onClick={() => {
                        if (!canceled) {
                          handleProductStatUpdate(selectedOrder, index);
                        }
                      }}
                    >
                      <span
                        className={cx(
                          'font-semibold',
            canceled ? 'kd-canceled-line' : 'text-slate-800 dark:text-white',
            prepared && 'kd-struck-line font-medium text-slate-400 dark:text-neutral-400'
                        )}
                        style={canceled ? CANCELED_STRIKE_STYLE : prepared ? PREPARED_STRIKE_STYLE : undefined}
                      >
                        <span
                          className={cx(
                            'kd-card-product-qty mr-1 inline-flex min-w-[1.5rem] justify-center rounded-md px-1.5 py-0.5',
                            canceled ? 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-300' : 'bg-slate-900/5 text-slate-700 dark:bg-neutral-800 dark:text-neutral-100'
                          )}
                        >
                          {product.total_quantity}
                        </span>
                        {product.designation}
                      </span>
                      {product.note && (
                        <span
                          className={cx(
                            'kd-card-product-note mt-1.5 flex items-start gap-1.5 font-semibold',
                            canceled ? 'kd-canceled-line' : 'text-orange-600 dark:text-orange-300'
                          )}
                          style={canceled ? CANCELED_STRIKE_STYLE : undefined}
                        >
                          <StickyNote className="kd-card-icon-sm mt-0.5 shrink-0" strokeWidth={2.25} aria-hidden="true" />
                          {product.note}
                        </span>
                      )}
                      {formuleItems.length > 0 && (
                        <div
                          className={cx(
                            'kd-card-product-formule mt-1.5 space-y-0.5 border-l-2 pl-2.5 font-medium',
                            canceled ? 'kd-canceled-line border-red-300 dark:border-red-800' : 'border-slate-200 text-slate-500 dark:border-neutral-700 dark:text-neutral-300'
                          )}
                          style={canceled ? CANCELED_STRIKE_STYLE : undefined}
                        >
                          {formuleItems.map((item, formuleIdx) => (
                            <div key={formuleIdx}>{item}</div>
                          ))}
                        </div>
                      )}
                      {buttonLoading[`product-${selectedOrder.id_ticket_cle[index]}-${index}`] && (
                        <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-kd-spin text-kd-brand" />
                      )}
                    </li>
                  );
                })}
              </ul>
              {renderPreviousProducts(selectedOrder, selectedOrder.id_ticket_cle.join(','), {
                alwaysExpanded: true,
              })}
            </div>

            <OrderCardFooter
              order={selectedOrder}
              theme={popupTheme}
              busy={Boolean(buttonLoading[selectedOrder.id_ticket_cle.join(',')])}
              onReady={() => handleDone(selectedOrder.id_ticket_cle.join(','), selectedOrder)}
              onBack={() => handleBack(selectedOrder.id_ticket_cle.join(','), selectedOrder)}
              className="px-5 py-3"
            />
          </div>
        </div>
        );
      })()}

      <footer className="fixed bottom-0 left-0 right-0 z-[500] flex flex-col items-center justify-between gap-2 border-t border-kd-line/80 bg-white/95 px-3 py-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] shadow-[0_-4px_16px_rgba(15,23,42,0.06)] backdrop-blur dark:border-neutral-800 dark:bg-black/95 dark:shadow-[0_-4px_16px_rgba(0,0,0,0.5)] sm:flex-row sm:px-4 md:px-5">
        <p className="text-xs font-semibold tracking-wide text-kd-muted dark:text-neutral-400">
          LACAISSE.MA © {new Date().getFullYear()}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <FreshnessIndicator lastUpdatedAt={lastUpdatedAt} />
          <div className={cx('rounded-full px-3 py-1 text-xs font-semibold', connectionTone)}>
            {connectionStatus === 'connected'
              ? 'Connecté'
              : connectionStatus === 'connecting'
                ? 'Connexion...'
                : connectionStatus === 'disconnected'
                  ? 'Déconnecté'
                  : 'Erreur'}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default KitchenDisplay;
